// Locked pilots of the Email module: campaigns shown for review that can never
// be sent (the backend's `src/email_campaigns/pilot.py`). Pure: no React, no
// runtime import — `scripts/tests/pilot-review.test.mjs` runs it under node:test.

/** The lock a pilot carries: shown for review, never activated or launched. */
export interface ExecutionLock {
  kind: string;
  reason: string;
  locked_at: string | null;
}

export interface PilotFact {
  text: string;
  source: string;
  where: string;
  origin: string;
  status: string;
}

export interface PilotItem {
  kind: string;
  where: string;
  what: string;
  detail?: string;
  source?: string;
  decision?: string;
}

export interface PilotEmail {
  position: number;
  label: string;
  day: number;
  function: string;
  variant: "A" | "B" | null;
  template_ref: string;
  subject: string;
  body: string;
  words: number;
  limit: string;
  length_issue: string;
  status: string;
  flags: string[];
  states: string[];
  decisions: string[];
  decisions_via_note: string[];
  personalization: PilotItem[];
  pending: PilotItem[];
  six_steps_kept: boolean | null;
  alternative: { label: string; subject: string; body: string; status: string } | null;
  notes: string[];
}

export interface PilotLead {
  code: string;
  contact_id: number;
  company_id: number;
  company: string;
  company_legal_name: string;
  director: string;
  /** The contact's name as Prism stores it, when it differs from the verified one. */
  prism_contact_name?: string | null;
  title: string | null;
  role: string;
  sector: string;
  naf: string;
  city: string;
  track: string;
  track_label: string;
  variant: "A" | "B";
  variant_label: string;
  commercial_status: string | null;
  facts: PilotFact[];
  reserves: string[];
  cannot_assert: string[];
  routing: {
    status: string;
    conditions: string;
    age: string;
    age_source: string;
    holding: string | null;
    mirror_note: string | null;
  };
  benchmark: {
    margin: number | null;
    median: number | null;
    q3: number | null;
    n: number | null;
    scope: string | null;
    indicator: string;
    short_email_claim: string;
  };
  states: {
    draft_in_prism: boolean;
    text_reviewed: boolean;
    ppa_approved: boolean;
    commercially_eligible: boolean;
    email_verified: boolean;
    send_authorized: boolean;
    deliverability: string;
    lemlist_suppression: string;
  };
  review_comments: string[];
  approval: string;
  emails: PilotEmail[];
}

export interface PilotArm {
  key: "A" | "B";
  label: string;
  template_ref: string;
  subject_template: string;
  body_template: string;
  structure: string[];
  leads: string[];
}

export interface PilotDecision {
  id: string;
  title: string;
  question: string;
  proposal: string;
  emails: number;
}

export interface PilotRecord {
  profile?: "CFO_US_GROWTH_V1";
  editorial?: { revision: number; version: string };
  draft_sync_manifest?: { operation: string };
  version: number;
  pilot_set: string;
  pilot_id: string;
  track: string;
  track_label: string;
  kind: "PILOT" | "EDITORIAL_SCENARIO_D_NOT_QUALIFIED" | "EDITORIAL_SCENARIO_CFO_TBD";
  source: { document: string; drafts: string; drafts_csv_sha256: string };
  audience: string;
  objective: string;
  states: {
    validation: string;
    deliverability: string;
    lemlist_suppressions: string;
    sends: number;
    scheduled_dates: number;
    send_authorization: string;
  };
  d_flags: string[];
  ab_test: {
    step: number;
    configured: boolean;
    executed: boolean;
    arms: PilotArm[];
    allocation: string;
    split: Record<string, number>;
    follow_ups_shared: boolean;
    follow_ups_note: string;
    metrics: { key: string; label: string; value: number | null }[];
    positive_reply_definition: string[];
    not_a_criterion: string;
    reading_rule: { min_sends_per_arm: number; min_uplift: number; text: string };
    caveats: string[];
    winner: string | null;
  };
  cadence: {
    steps: { position: number; label: string; day: number; function: string }[];
    sending_days: string;
    window: string;
    timezone: string;
    postponement: string;
    enabled: boolean;
    executable_events: number;
    execution_rule: string;
  };
  stop_rules: { event: string; rule: string }[];
  stop_note: string;
  decisions: PilotDecision[];
  documents: { name: string; role: string }[];
  launch_conditions: string[];
  leads?: PilotLead[];
  scenarios?: PilotLead[];
  bundle_fingerprint?: string;
}

/** A campaign nothing can change nor send: a locked pilot or a previous team's history. */
export function campaignReadOnly(campaign: {
  execution_lock?: ExecutionLock | null;
  provider_legacy?: boolean;
}): boolean {
  return !!campaign.execution_lock || !!campaign.provider_legacy;
}

/** The people of a pilot: its leads, or the scenarios of a not-qualified demo. */
export function pilotPeople(record: PilotRecord): PilotLead[] {
  return record.leads ?? record.scenarios ?? [];
}

export type AnnotationKind = "missing" | "proof" | "case" | "decision" | "contradicted" | "conflict" | "other";

export interface TextSegment {
  text: string;
  annotation: AnnotationKind | null;
}

const ANNOTATION = /\[\[([\s\S]*?)\]\]/g;

/** What an annotation `[[…]]` of a draft stands for, from its first words. */
export function annotationKind(inner: string): AnnotationKind {
  const head = inner.trim().toUpperCase();
  if (head.startsWith("MANQUANT")) return "missing";
  if (head.startsWith("À PROUVER") || head.startsWith("A PROUVER")) return "proof";
  if (head.startsWith("CAS RÉEL REQUIS") || head.startsWith("CAS REEL REQUIS")) return "case";
  if (head.startsWith("À ARBITRER") || head.startsWith("A ARBITRER")) return "decision";
  if (head.startsWith("CONTREDIT")) return "contradicted";
  if (head.startsWith("CONTRADICTION")) return "conflict";
  return "other";
}

/** A draft cut into plain text and its draft annotations (`[[…]]`, never sendable). */
export function annotationSegments(text: string): TextSegment[] {
  const out: TextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(ANNOTATION)) {
    const start = match.index ?? 0;
    if (start > last) out.push({ text: text.slice(last, start), annotation: null });
    out.push({ text: match[0], annotation: annotationKind(match[1] ?? "") });
    last = start + match[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), annotation: null });
  return out;
}

/** A draft's length verdict, in words. */
export function lengthIssueLabel(issue: string): string {
  if (!issue) return "";
  if (issue === "DOCUMENT") return "over the limit with the document's own examples (DEC-05)";
  return issue.toLowerCase().split("_").join(" ");
}

export function annotationCount(text: string): number {
  return [...text.matchAll(ANNOTATION)].length;
}

export const ANNOTATION_LABEL: Record<AnnotationKind, string> = {
  missing: "Missing value",
  proof: "Proof required",
  case: "Real case required",
  decision: "PPA decision",
  contradicted: "Contradicted by the data",
  conflict: "Document conflict",
  other: "Annotation",
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * The day an email would actually go, for a relative day counted from a
 * hypothetical day 0: the first allowed weekday on or after the relative day.
 * `sendingDays` are ISO weekdays (1 = Monday … 7 = Sunday). Pure, UTC dates.
 */
export function plannedDay(start: Date, relativeDay: number, sendingDays: number[]): { date: Date; shifted: number } {
  const allowed = new Set(sendingDays.map((d) => d % 7));
  const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + relativeDay));
  for (let shift = 0; shift < 7; shift += 1) {
    const day = new Date(target.getTime() + shift * 86_400_000);
    if (allowed.has(day.getUTCDay())) return { date: day, shifted: shift };
  }
  return { date: target, shifted: 0 };
}

export function weekdayName(date: Date): string {
  return WEEKDAYS[date.getUTCDay()];
}

/** The next allowed day on or after `from` — a hypothetical day 0 for the example. */
export function nextAllowedDay(from: Date, sendingDays: number[]): Date {
  return plannedDay(from, 0, sendingDays).date;
}

/** Real provider receipts; absent fields mean not checked, never zero. */
export interface PilotDraftSync {
  status: "COMPLETED" | "PARTIAL";
  updated_at?: string;
  activation_authorized: false;
  provider_url?: string;
  provider_campaign_id?: string;
  leads_imported?: number;
  personalizations_verified?: number;
  steps?: number;
  ab_test?: string;
  safety?: {
    campaign_status: string;
    auto_review: false;
    senders: number;
    launched_leads: number;
    sent_emails: number;
    scheduled_emails: number;
    scheduled_count_basis: string;
  };
  failures: { contact_id?: number; reason: string; code?: string }[];
  contacts?: { contact_id: number; lead_id: string | null; status: string | null;
    personalizations: number; variant: string;
    suppression: { status: string; reason: string | null } | null }[];
}

export function canSyncPilotDraft(
  access: { can_sync: boolean; can_manage: boolean },
  campaign: { status: string; execution_lock?: ExecutionLock | null;
    pilot?: { draft_sync_manifest?: { operation?: string } } | null },
  external: boolean,
): boolean {
  return external && access.can_sync && access.can_manage && campaign.status === "DRAFT"
    && !!campaign.execution_lock
    && campaign.pilot?.draft_sync_manifest?.operation === "SYNC_DRAFT_TO_LEMLIST";
}

/** A J0 preview must use a person assigned to that arm; follow-ups are shared. */
export function previewPeopleForTemplate<T extends { variant: string }>(people: T[], key: string): T[] {
  return key.startsWith("1:") ? people.filter((person) => person.variant === key.slice(2)) : people;
}

/** Profile labels share the established reader and canvas; no second workflow. */
export function pilotVariantLabel(record: PilotRecord | null | undefined, arm: "A" | "B") {
  return record?.profile === "CFO_US_GROWTH_V1"
    ? arm === "A" ? "Variante 1" : "Variante 2"
    : arm === "A" ? "A · Courte" : "B · Neurosud";
}
export function pilotVariantKeys(record: PilotRecord): ("A" | "B")[] {
  return record.profile === "CFO_US_GROWTH_V1" ? record.ab_test.arms.map((a) => a.key) : ["A", "B"];
}
